/* eslint-disable @typescript-eslint/no-this-alias */
import * as THREE from 'three';
import {
  CustomParamsCropType,
  DataFileType,
  DrawingSubstrateType,
  GetCropType,
  ImageForTextureType,
  PruningObjectType,
} from '../types';
import { PruningObject } from './PruningObject';

interface UVSType {
  cropX?: number;
  cropY?: number;
  lengthX?: number;
  lengthY?: number;
}

export class DrawingSubstrate extends THREE.Mesh implements DrawingSubstrateType {
  private width = 0;
  private height = 0;
  private imgOrigin = {
    width: 0,
    height: 0,
  };
  private oldCrop = {
    offsetX: 0,
    offsetZ: 0,
    lengthX: 0,
    lengthY: 0,
  };
  base64data = '';
  material: THREE.MeshBasicMaterial;
  pruningObject: PruningObjectType | null = null;
  paramsPruning: CustomParamsCropType | null = null;
  constructor(fileImg: DataFileType) {
    super();
    this.type = 'drawing-substrate';
    this.name = 'drawing-substrate';
    this.material = new THREE.MeshBasicMaterial();

    this.buildGeometry(fileImg).then();
  }

  async buildGeometry(fileImg: DataFileType) {
    let image: ImageForTextureType;
    if (fileImg instanceof File) {
      image = await this.getImageForTexture(fileImg);
    } else {
      image = { ...fileImg };
    }

    const loader = new THREE.TextureLoader();
    // loader.setCrossOrigin('anonymous');
    this.base64data = image.base64data;
    loader.load(image.base64data, texture => {
      const pov = image.width.toString().length - 1;
      const scale = (Math.trunc(image.width / 10 ** pov) * 10 ** pov) / 10;

      this.imgOrigin.width = image.width / scale;
      this.imgOrigin.height = image.height / scale;
      this.width = image.width / scale;
      this.height = image.height / scale;
      this.geometry = new THREE.PlaneGeometry(this.width, this.height, 1);
      this.assignUVs({
        cropX: 0,
        cropY: 0,
        lengthX: 1,
        lengthY: 1,
      });
      this.geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));
      if (this.material) {
        this.material.dispose();
      }
      this.material = new THREE.MeshBasicMaterial({
        color: '#ffffff',
        map: texture,
        transparent: true,
        opacity: 1,
      });
    });
  }
  private rebuildGeometry() {
    if (this.paramsPruning) {
      const {
        widthCroup,
        heightCroup,
        cropX,
        cropY,
        lengthX,
        lengthY,
        offsetX,
        offsetZ,
        offsetWidth = 0,
        offsetHeight = 0,
      } = this.paramsPruning;
      this.width = widthCroup;
      this.height = heightCroup;
      this.geometry.dispose();
      this.geometry = new THREE.PlaneGeometry(this.width, this.height, 1);
      this.geometry.applyMatrix4(new THREE.Matrix4().makeRotationX(-Math.PI / 2));

      this.oldCrop.offsetX += offsetX;
      this.oldCrop.offsetZ += offsetZ;
      this.oldCrop.lengthX += offsetWidth;
      this.oldCrop.lengthY += offsetHeight;

      this.assignUVs({
        cropX,
        cropY,
        lengthX,
        lengthY,
      });
    }
  }
  createPruningObject() {
    this.pruningObject = new PruningObject({
      p0: new THREE.Vector3(-this.width / 2, 0, this.height / 2),
      p1: new THREE.Vector3(-this.width / 2, 0, -this.height / 2),
      p2: new THREE.Vector3(this.width / 2, 0, -this.height / 2),
      p3: new THREE.Vector3(this.width / 2, 0, this.height / 2),
    });
    this.add(this.pruningObject);
  }
  runCropAction(paramsPruning?: CustomParamsCropType) {
    if (this.pruningObject) {
      this.paramsPruning = { ...this.paramsPruning, ...this.pruningObject.cropParams };
      this.rebuildGeometry();
      this.removePruningObj();
    } else if (paramsPruning) {
      this.paramsPruning = { ...paramsPruning };
      this.rebuildGeometry();
    }
  }
  getCropParams(): GetCropType {
    return {
      imgOriginWidth: this.imgOrigin.width,
      imgOriginHeight: this.imgOrigin.height,
      offsetX: this.oldCrop.offsetX,
      offsetZ: this.oldCrop.offsetZ,
      lengthX: this.oldCrop.lengthX,
      lengthY: this.oldCrop.lengthY,
    };
  }
  updateScale(coefficient: number) {
    this.scale.set(coefficient, 1, coefficient);
    if (!this.paramsPruning) {
      this.paramsPruning = {
        cropX: 0,
        lengthY: 0,
        offsetZ: 0,
        heightCroup: 0,
        offsetHeight: 0,
        offsetWidth: 0,
        widthCroup: 0,
        offsetX: 0,
        lengthX: 0,
        cropY: 0,
        coefficient,
      };
    } else {
      this.paramsPruning = { ...this.paramsPruning, coefficient };
    }
  }
  removePruningObj() {
    if (this.pruningObject) {
      this.pruningObject.removePruningObj();
      this.pruningObject = null;
    }
  }
  removeMesh() {
    if (this.pruningObject) {
      this.removePruningObj();
    }
    this.geometry.dispose();
    if (this.material && this.material?.map) {
      this.material.map.dispose();
      this.material.map = null;
      this.material.dispose();
    }

    // mesh.material = null;
    this?.parent?.remove(this);
  }

  private getImageForTexture(fileImg: File): Promise<ImageForTextureType> {
    const scope = this;
    return new Promise(resolve => {
      const img = new Image();
      const imageURL = URL.createObjectURL(fileImg);

      img.onload = () => {
        const width = img.width;
        const height = img.height;
        resolve({
          height,
          width,
          base64data: scope.getBase64Image(img),
        });
        URL.revokeObjectURL(imageURL);
      };
      // img.crossOrigin = 'anonymous';
      img.src = imageURL;
    });
  }
  private getBase64Image(img: HTMLImageElement): string {
    const canvas = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(img, 0, 0);
      return canvas.toDataURL('image/png');
    }
    return '';
  }
  private assignUVs({ cropX = 0, cropY = 0, lengthX = 0, lengthY = 0 }: UVSType) {
    const geometry = this.geometry as THREE.PlaneGeometry;
    // начало координат с левого нижнего угла

    const ver0 = new THREE.Vector2(cropX, lengthY);
    const ver1 = new THREE.Vector2(lengthX, lengthY);
    const ver2 = new THREE.Vector2(cropX, cropY);
    const ver3 = new THREE.Vector2(lengthX, cropY);
    geometry.faceVertexUvs[0] = [];
    //0, 2, 1
    geometry.faceVertexUvs[0].push([
      // new THREE.Vector2(0, 1), // 0
      // new THREE.Vector2(0, 0), // 2
      // new THREE.Vector2(1, 1), // 1
      ver0,
      ver2,
      ver1,
    ]);
    //2, 3, 1
    geometry.faceVertexUvs[0].push([
      // new THREE.Vector2(0, 0), // 2
      // new THREE.Vector2(1, 0), // 3
      // new THREE.Vector2(1, 1), // 1
      ver2,
      ver3,
      ver1,
    ]);
    /*if (!copy) {
    } else {
      geometry.faceVertexUvs[0][0][0].copy(ver0);
      geometry.faceVertexUvs[0][0][1].copy(ver2);
      geometry.faceVertexUvs[0][0][2].copy(ver1);

      geometry.faceVertexUvs[0][1][0].copy(ver2);
      geometry.faceVertexUvs[0][1][1].copy(ver3);
      geometry.faceVertexUvs[0][1][2].copy(ver1);
    }*/

    geometry.uvsNeedUpdate = true;
  }
}
